import React from 'react';
import { useTranslation, Trans } from 'react-i18next'; // <-- Import hook and Trans
import { HelpCircle, CheckCircle, CreditCard, Banknote, Wallet } from 'lucide-react'; // Import CheckCircle
// Import Recharts components
import { PieChart, Pie, Cell, ResponsiveContainer, Label } from 'recharts'; 
import './InvestorOfferSection.css'; // Ensure CSS is imported

function InvestorOfferSection() {
  const { t } = useTranslation(); // <-- Initialize hook

  const scrollToContact = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openFAQModal = (e) => {
    e.preventDefault();
    // Find and click the FAQ button
    const faqButton = document.querySelector('.floating-faq-button');
    if (faqButton) {
      faqButton.click();
    }
  };

  // Data for the allocation chart - REVERSED for horizontal mirroring
  const allocationData = [
    { name: t('fundAllocationOps'), value: 10, key: 'fundAllocationOps' },      // Was last
    { name: t('fundAllocationRandD'), value: 25, key: 'fundAllocationRandD' }, // Was third
    { name: t('fundAllocationNewHires'), value: 35, key: 'fundAllocationNewHires' },           // Was second
    { name: t('fundAllocationMarketing'), value: 30, key: 'fundAllocationMarketing' },  // Was first
  ];

  // Colors for the chart segments - Use nuances of primary color
  const COLORS = ['#17a4b0', '#7ee0e9', '#4dd0da', '#1cbeca']; // Shades of #1cbeca

  return (
    // Using content-panel for consistency, but could be a custom styled section
    <section id="investor-offer" className="investor-offer-section content-panel">
      <h2 className="market-headline">{t('investorOpportunityLinkText')}</h2>
      <div className="investor-positioning-statement">
        <p>{t('investorOfferIntroP1')}</p>
      </div>
            {/* --- NEW Investment Ask Subsection --- */}
            <div className="investment-ask-subsection">
        {/* Section intro for investor ask */}
        <h2 className="market-headline">{t('investmentEquityPercent')}</h2>
        <div className="market-subline" style={{textAlign: 'center', color: '#b0c4d4', fontSize: '1.08rem', fontWeight: 400, margin: '-0.7rem auto 2.2rem auto', maxWidth: 600}}>
          {t('investmentEquityText')}
        </div>
        <div className="investment-ask-content">
          
          {/* Right Column: Investment Terms (now first) */}
          <div className="investment-ask-right">
            <h3 className="subsection-title">{t('investmentTermsTitle', 'Investment Terms')}</h3>
            <div className="terms-list">
              <div className="term-item">
                <div className="term-value">{t('investmentPrivateSaleAmount')}</div>
                <div className="term-description">{t('investmentPrivateSaleRound')}</div>
              </div>
              <div className="term-item">
                <div className="term-value">{t('investmentPublicSaleAmount')}</div>
                <div className="term-description">{t('investmentPublicSaleRound')}</div>
              </div>
              <div className="term-item">
                <div className="term-value2">{t('investmentDividendsPercent')}</div>
                <div className="term-description">{t('investmentDividendsText')}</div>
              </div>
            </div>

            {/* --- NEW Investment Details (within investment-ask-right) --- */}
            <div className="new-investment-details">
              <h4 className="subsection-title small-title">{t('investmentDetailsTitle')}</h4>
              <div className="new-details-list">
                <div className="new-detail-item">
                  <p><strong>{t('investmentDetailsPrivateRoundTitle')}:</strong> {t('investmentDetailsPrivateRoundText')}</p>
                </div>
                <div className="new-detail-item">
                  <p><strong>{t('investmentDetailsTokenomicsTitle')}:</strong> <a href="#" onClick={openFAQModal} className="modal-link">{t('investmentDetailsTokenomicsLink')}</a></p>
                </div>
                <div className="new-detail-item">
                  <div className="payment-methods">
                    <div className="payment-method">
                      <CreditCard size={20} />
                      <span>{t('paymentMethodCreditCard')}</span>
                    </div>
                    <div className="payment-method">
                      <Banknote size={20} />
                      <span>{t('paymentMethodBankTransfer')}</span>
                    </div>
                    <div className="payment-method">
                      <Wallet size={20} />
                      <span>{t('paymentMethodCrypto')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* --- END NEW Investment Details --- */}

          </div>

          {/* Left Column: Chart and Allocations (now second) */}
          <div className="investment-ask-left">
            <h3 className="subsection-title">{t('investmentAskTitle', 'Investment Ask')}</h3>
            <div className="allocation-chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={allocationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60} // Creates the donut hole
                    outerRadius={100} // Size of the chart
                    fill="#8884d8"
                    paddingAngle={5} // Space between segments
                    dataKey="value"
                    labelLine={false} // Hide connector lines for labels
                  >
                    {allocationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  {/* Optionally add center text if needed in future */}
                   {/* <Label value={t('raisingAmount', '€X M')} position="center" className="chart-center-label" /> */}
                </PieChart>
              </ResponsiveContainer>
              {/* Allocation Labels - positioned via CSS */}
              <div className="allocation-labels">
                {allocationData.map((entry, index) => (
                   <div key={entry.key} className={`label-item label-item-${index}`}>
                      <span className="label-percent" style={{ color: COLORS[index % COLORS.length] }}>{entry.value}%</span>
                      <span className="label-text">{t(entry.key, entry.name)}</span>
                   </div>
                ))}
              </div>
            </div>
            {/* Text moved below the chart container */}
            <div className="section-intro" style={{ marginTop: '1.5rem', textAlign: 'center', maxWidth: '350px', margin: '1.5rem auto 0 auto' }}>
               <p>{t('investorOfferIntroP2')}</p>
            </div>
          </div>

        </div>
      </div>
      {/* --- END NEW Investment Ask Subsection --- */}

      {/* New Row Container for Unique Offer and Final CTA */}
      <div className="offer-cta-row">
        
        {/* Unique Offer Box (within its original info-grid wrapper) */}
        <div className="info-grid">
          <div className="info-box unique-offer">
            <h3 className="box-title">{t('investorOfferUniqueTitle')}</h3>
            <ul className="box-list">
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferUniqueItem1')}</li>
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferUniqueItem2')}</li>
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferUniqueItem3')}</li>
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferUniqueItem4')}</li>
            </ul>
          </div>
        </div>

        {/* Final CTA Box */}
        <div className="final-cta-box">
          <h3 className="cta-title">{t('investorOfferCtaTitle')}</h3>
          <p className="cta-subtitle">{t('investorOfferCtaSubtitle')}</p>
          <a
            href="#contact"
            onClick={scrollToContact}
            className="cta-button final-cta-button"
          >
            {t('investorOfferCtaButton')}
          </a>
        </div>

      </div> {/* End offer-cta-row */}

      {/* FAQ Link */}
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <a
          href="#"
          onClick={openFAQModal}
          style={{ 
            color: 'var(--color-primary)',
            textDecoration: 'none',
            fontSize: '0.95rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <HelpCircle size={16} />
          {t('faqButtonText')}
        </a>
      </div>

    </section>
  );
}

export default InvestorOfferSection; 